import {
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
  Button,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Card,
  CardActionArea,
  useMediaQuery,
} from '@material-ui/core';
import { FC, useState } from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import Amex from '../Amex.svg';
import Diners from '../Diners.svg';
import Discover from '../Discover.svg';
import Mastercard from '../Mastercard.svg';
import Jcb from '../Jcb.svg';
import Visa from '../Visa.svg';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import BankIcon from '@material-ui/icons/AccountBalance';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import { CardBrand, PayerPaymentMethod, PaymentMethodStatus, PaymentMethodType } from '../gql-types.generated';
import { CardBrandForDisplay } from '../util/CardBrandForDisplay';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    errorGrid: {
      backgroundColor: '#FFF7F7',
      padding: theme.spacing(1, 2),
    },
    errorText: {
      marginLeft: theme.spacing(1),
      maxWidth: '80%',
    },
    gridItem: {
      padding: theme.spacing(0.5, 2),
    },
    fontWeight: {
      fontWeight: 400,
    },
    cardContainer: {
      padding: theme.spacing(0, 2, 2, 2),
    },
    methodCard: {
      width: '100%',
      height: '100%',
      border: 'solid 1.5px #0275d8',
      borderRadius: theme.spacing(2),
    },
    methodCardSelected: {
      width: '100%',
      height: '100%',
      border: 'solid 3px #0275d8',
      borderRadius: theme.spacing(2),
      backgroundColor: '#EEF4FE',
    },
    methodCardVerifying: {
      width: '100%',
      height: '100%',
      border: 'solid 1.5px #0275d8',
      borderRadius: theme.spacing(2),
      backgroundColor: '#F3F3F3',
    },
    methodCardSelectedVerifying: {
      width: '100%',
      height: '100%',
      border: 'solid 3px #0275d8',
      borderRadius: theme.spacing(2),
      backgroundColor: '#F3F3F3',
    },
    methodCardUnverified: {
      width: '100%',
      height: '100%',
      border: 'solid 1.5px #0275d8',
      borderRadius: theme.spacing(2),
      backgroundColor: '#FFF7F7',
    },
    methodCardSelectedUnverified: {
      width: '100%',
      height: '100%',
      border: 'solid 3px #0275d8',
      borderRadius: theme.spacing(2),
      backgroundColor: '#FFF7F7',
    },
    methodCardActionContainer: {
      padding: theme.spacing(0, 2, 2, 2),
    },
    cardActionContainer: {
      padding: theme.spacing(2),
    },
    cardText: {
      fontWeight: 'bold',
      fontSize: '14px',
    },
    cardTextGrey: {
      fontSize: '14px',
      color: 'grey',
      fontWeight: 'bold',
    },
    methodIcon: {
      textAlign: 'end',
    },
    cardButton: {
      padding: theme.spacing(1, 0.5, 0, 0.5),
    },
    deleteButton: {
      color: theme.palette.getContrastText('#f44336'),
      backgroundColor: '#f44336',
      '&:hover': {
        backgroundColor: '#d32f2f',
      },
    },
    addText: {
      textAlign: 'center',
      fontSize: '15px',
      fontWeight: 'bold',
      color: '#0275d8',
    },
    cardIcon: {
      fontSize: '48px',
      color: '#0275d8',
    },
    addIconContainer: {
      textAlign: 'center',
    },
    deleteIcon: {
      padding: 0,
      margin: 0,
    },
    creditCardContainer: {
      padding: theme.spacing(0, 1, 2, 2),
    },
    bankCardContainer: {
      padding: theme.spacing(0, 2, 2, 1),
    },
    paymentMethodCard: {
      width: '100%',
      height: '100%',
      border: 'solid 1.5px #0275d8',
      borderRadius: theme.spacing(2),
      backgroundColor: '#276EF114',
    },
    cardBorder: {
      borderBottom: 'solid 1.5px #0275d8',
      margin: theme.spacing(0, -2, 0, 2),
      paddingRight: theme.spacing(2),
    },
    selectedCardBorder: {
      borderBottom: 'solid 3px #0275d8',
      margin: theme.spacing(0, -2, 0, 2),
      paddingRight: theme.spacing(2),
    },
    cardTextBlue: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#0275d8',
    },
    unverifiedText: {
      fontWeight: 'bold',
      fontSize: '14px',
      color: theme.palette.error.main,
    },
    verifyingText: {
      fontWeight: 'bold',
      fontSize: '14px',
    },
    brandArea: {
      alignItems: 'center',
      paddingBottom: theme.spacing(2),
    },
  }),
);
interface PaymentMethodsProps {
  deleteMethod: (paymentMethod: PayerPaymentMethod) => void;
  paymentMethods?: PayerPaymentMethod[] | null;
  setAsDefault: (paymentMethod: PayerPaymentMethod) => void;
  deletePaymentMethodError?: Error;
  handleVerifyClick: (paymentMethod: PayerPaymentMethod) => void;
  handleAddCard: () => void;
  handleAddBank: () => void;
  forSelect?: boolean;
  handleSelectPaymentMethod?: (paymentMethod: PayerPaymentMethod) => void;
  selectedPaymentMethod?: PayerPaymentMethod;
  close?: () => void;
  paymentMethodError?: Error;
}

export const getPaymentMethodText = (paymentMethod: PayerPaymentMethod): string => {
  if (paymentMethod.paymentMethod?.type === PaymentMethodType.CreditCard) {
    return `Card ending in ${paymentMethod.paymentMethod?.creditCard?.lastFour}`;
  }
  return `Account ending in ${paymentMethod.paymentMethod?.paymentBank?.lastFour}`;
};

export const getPaymentMethodIcon = (
  paymentMethod: PayerPaymentMethod,
  isDisabled: boolean,
  length: number,
  isCard?: boolean,
): JSX.Element => {
  if (paymentMethod.paymentMethod?.type === PaymentMethodType.PaymentBankUs) {
    return (
      <BankIcon
        style={{
          height: isCard ? length : length / 1.3,
          width: isCard ? length : length / 1.3,
          opacity: isDisabled ? 0.5 : 1,
          padding: isCard ? (length - length / 1.3) / 2 : 0,
        }}
      />
    );
  }
  const cardBrand = paymentMethod.paymentMethod?.creditCard?.cardBrand;
  if (cardBrand === CardBrand.Amex) {
    return <img src={Amex} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }}></img>;
  }
  if (cardBrand === CardBrand.Diners) {
    return <img src={Diners} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }}></img>;
  }
  if (cardBrand === CardBrand.Discover) {
    return <img src={Discover} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }}></img>;
  }
  if (cardBrand === CardBrand.Jcb) {
    return <img src={Jcb} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }}></img>;
  }
  if (cardBrand === CardBrand.Mastercard) {
    return <img src={Mastercard} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }}></img>;
  }
  if (cardBrand === CardBrand.Visa) {
    return <img src={Visa} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }}></img>;
  }
  return <HelpOutlineIcon />;
};

const PaymentMethods: FC<PaymentMethodsProps> = props => {
  const classes = useStyles();
  const {
    deleteMethod,
    paymentMethods,
    setAsDefault,
    deletePaymentMethodError,
    handleVerifyClick,
    handleAddCard,
    handleAddBank,
    handleSelectPaymentMethod,
    forSelect,
    selectedPaymentMethod,
    close,
  } = props;
  const [selectedMethod, setSelectedMethod] = useState<PayerPaymentMethod | null>(null);

  const matchesXsDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const MAX_CARDS = 5;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const MAX_BANKS = 1;
  let cardAmount = 0;
  let bankAmount = 0;
  paymentMethods?.forEach(method => {
    if (method.paymentMethod?.type === PaymentMethodType.CreditCard) {
      cardAmount += 1;
    }
    if (method.paymentMethod?.type === PaymentMethodType.PaymentBankUs) {
      bankAmount += 1;
    }
  });

  const handleCardClicked = (paymentMethod: PayerPaymentMethod) => {
    if (paymentMethod === selectedMethod) {
      setSelectedMethod(null);
    } else {
      setSelectedMethod(paymentMethod);
    }
  };

  const handleSetDefaultClick = () => {
    if (selectedMethod) {
      setAsDefault(selectedMethod);
    }
  };

  const handleDeleteClick = () => {
    if (selectedMethod) {
      deleteMethod(selectedMethod);
    }
  };

  const bankAccount = paymentMethods?.find(method => {
    return method.paymentMethod?.type === PaymentMethodType.PaymentBankUs;
  });

  const handleChange = (event: { target: { value: string } }) => {
    if (forSelect) {
      const paymentMethodId = event.target.value;
      const paymentMethod = paymentMethods?.find(method => {
        return method?.paymentMethod?.id === paymentMethodId;
      });
      if (paymentMethod && handleSelectPaymentMethod) {
        handleSelectPaymentMethod(paymentMethod);
      }
      if (close) {
        close();
      }
    }
  };

  if (forSelect && handleSelectPaymentMethod) {
    return (
      <Grid container xs={12}>
        <FormControl>
          <RadioGroup value={selectedPaymentMethod} onChange={handleChange}>
            {paymentMethods &&
              paymentMethods.map(method => {
                return (
                  <Grid container item xs={12} key={method.paymentMethod?.id} className={classes.gridItem}>
                    <FormControlLabel
                      key={method.paymentMethod?.id}
                      value={method.paymentMethod?.id}
                      control={<Radio color="primary" />}
                      label={
                        <Grid item container alignItems="center" className={classes.fontWeight}>
                          {getPaymentMethodIcon(method, false, 48)}
                          <Typography>{getPaymentMethodText(method)}</Typography>
                        </Grid>
                      }
                    />
                  </Grid>
                );
              })}
          </RadioGroup>
        </FormControl>
        {cardAmount < MAX_CARDS && (
          <Grid item container xs={12} className={classes.gridItem}>
            <Button
              variant="text"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                handleAddCard();
              }}
              data-cy="add-credit-card"
            >
              ADD CREDIT CARD
            </Button>
          </Grid>
        )}
        {bankAmount < MAX_BANKS && (
          <Grid item container xs={12} className={classes.gridItem}>
            <Button variant="text" color="primary" startIcon={<AddIcon />} onClick={handleAddBank} data-cy="add-bank-account">
              ADD BANK ACCOUNT
            </Button>
          </Grid>
        )}
      </Grid>
    );
  }

  return (
    <Grid container>
      {/* Show this error when there has been an error while deleting a payment method */}
      {deletePaymentMethodError && (
        <Grid item container xs={12} className={classes.errorGrid} alignItems="center">
          <ErrorOutlineIcon color="error" fontSize="small" />
          <Typography color="error" className={classes.errorText}>
            {deletePaymentMethodError.message}
          </Typography>
        </Grid>
      )}

      {/* Show this error when the bank account needs to be verified */}
      {bankAccount && bankAccount.paymentMethod?.status === PaymentMethodStatus.Unverified && (
        <Grid item container xs={12} className={classes.errorGrid} alignItems="center">
          <ErrorOutlineIcon color="error" fontSize="small" />
          <Typography color="error" className={classes.errorText}>
            Please verify bank account
          </Typography>
        </Grid>
      )}

      {/* Show this error when the bank verification has failed and needs to be redone */}
      {bankAccount && bankAccount.paymentMethod?.status === PaymentMethodStatus.VerificationFailed && (
        <Grid item container xs={12} className={classes.errorGrid} alignItems="center">
          <ErrorOutlineIcon color="error" fontSize="small" />
          <Typography color="error" className={classes.errorText}>
            Verification failed. Please verify bank account again.
          </Typography>
        </Grid>
      )}

      <Grid item container xs={12}>
        {paymentMethods &&
          paymentMethods.map(method => {
            const unverifiedBankAccount = (method.paymentMethod?.paymentBank &&
              (method.paymentMethod?.status === PaymentMethodStatus.Unverified ||
                method.paymentMethod?.status === PaymentMethodStatus.VerificationFailed)) as boolean;

            const pendingVerificationAccount = (method.paymentMethod?.paymentBank &&
              method.paymentMethod?.status === PaymentMethodStatus.VerificationPending) as boolean;

            const unverifiedCardClasses =
              selectedMethod === method ? classes.methodCardSelectedUnverified : classes.methodCardUnverified;

            const verifyingCardClasses = selectedMethod === method ? classes.methodCardSelectedVerifying : classes.methodCardVerifying;

            const verifyClasses = unverifiedBankAccount ? unverifiedCardClasses : verifyingCardClasses;

            const cardClasses = selectedMethod === method ? classes.methodCardSelected : classes.methodCard;

            return (
              <Grid item container key={paymentMethods?.indexOf(method)} className={classes.cardContainer}>
                <Grid
                  item
                  container
                  xs={12}
                  onClick={() => {
                    handleCardClicked(method);
                  }}
                  data-cy="payment-method-card"
                >
                  <Card
                    variant="outlined"
                    className={unverifiedBankAccount || pendingVerificationAccount ? verifyClasses : cardClasses}
                  >
                    <CardActionArea className={classes.methodCardActionContainer}>
                      <Grid item container xs={12} className={classes.brandArea}>
                        <Grid item xs={8}>
                          <Typography className={classes.cardTextGrey}>
                            {method.paymentMethod?.creditCard?.cardBrand &&
                              CardBrandForDisplay.find(data => data.key === method.paymentMethod?.creditCard?.cardBrand)?.value}
                            {method.paymentMethod?.paymentBank && 'Bank Account'}
                          </Typography>
                          {unverifiedBankAccount && <Typography className={classes.unverifiedText}>UNVERIFIED</Typography>}
                          {pendingVerificationAccount && <Typography className={classes.verifyingText}>VERIFYING</Typography>}
                        </Grid>
                        <Grid item xs={4} alignContent="center" className={classes.methodIcon}>
                          {getPaymentMethodIcon(method, false, matchesXsDown ? 60 : 65, true)}
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography className={classes.cardText}>{`****  ****  ****  ${
                          method.paymentMethod?.creditCard?.lastFour || method.paymentMethod?.paymentBank?.lastFour
                        }`}</Typography>
                      </Grid>
                      <Grid item container xs={12}>
                        <Grid item xs={8}>
                          <Typography className={classes.cardTextGrey} noWrap>
                            {method?.paymentMethod?.creditCard?.cardHolder?.holderName ||
                              method?.paymentMethod?.paymentBank?.accountHolder?.holderName}
                          </Typography>
                        </Grid>
                        <Grid item xs={4} className={selectedMethod === method ? classes.selectedCardBorder : classes.cardBorder}>
                          {method?.isDefault && <Typography className={classes.cardTextBlue}>DEFAULT</Typography>}
                        </Grid>
                      </Grid>
                    </CardActionArea>
                  </Card>
                </Grid>
                {selectedMethod === method && (
                  <Grid item container xs={12}>
                    <Grid item xs={5} sm={6} className={classes.cardButton}>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteClick}
                        classes={{ root: classes.deleteButton, startIcon: classes.deleteIcon }}
                        data-cy="delete-payment-method"
                      >
                        <Typography variant="subtitle2">Delete</Typography>
                      </Button>
                    </Grid>
                    <Grid item xs={7} sm={6} className={classes.cardButton}>
                      {unverifiedBankAccount ? (
                        <Button
                          color="primary"
                          variant="contained"
                          fullWidth
                          onClick={() => {
                            handleVerifyClick(method);
                          }}
                          data-cy="verify-account"
                        >
                          <Typography variant="subtitle2">Verify</Typography>
                        </Button>
                      ) : (
                        <Button
                          color="primary"
                          variant="contained"
                          fullWidth
                          onClick={handleSetDefaultClick}
                          disabled={method?.isDefault || false}
                          data-cy="make-default"
                        >
                          <Typography variant="subtitle2">Make Default</Typography>
                        </Button>
                      )}
                    </Grid>
                    <Grid item xs={6}></Grid>
                  </Grid>
                )}
              </Grid>
            );
          })}
      </Grid>
      {cardAmount < MAX_CARDS && (
        <Grid item container xs={6} className={classes.creditCardContainer}>
          <Grid
            item
            xs={12}
            onClick={() => {
              handleAddCard();
            }}
            data-cy="add-credit-card"
          >
            <Card variant="outlined" className={classes.paymentMethodCard}>
              <CardActionArea className={classes.cardActionContainer}>
                <Grid item xs={12} className={classes.addIconContainer}>
                  <CreditCardIcon className={classes.cardIcon} />
                </Grid>
                <Grid item xs={12} className={classes.addText}>
                  Add Card
                </Grid>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      )}
      {bankAmount < MAX_BANKS && (
        <Grid item container xs={6} className={classes.bankCardContainer}>
          <Grid item xs={12} onClick={handleAddBank} data-cy="add-bank-account">
            <Card variant="outlined" className={classes.paymentMethodCard}>
              <CardActionArea className={classes.cardActionContainer}>
                <Grid item xs={12} className={classes.addIconContainer}>
                  <BankIcon className={classes.cardIcon} />
                </Grid>
                <Grid item xs={12} className={classes.addText}>
                  Add Account
                </Grid>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default PaymentMethods;
